/* eslint-disable react-refresh/only-export-components */
import { FC, useState } from "react";

import { HoverableEditField } from "components/hover-widgets/EditableField";
import { HoverableToolbar } from "components/hover-widgets/HoverableToolbar";
import { Card } from "mds/components/Card";
import { ExpandableText } from "mds/components/ExpandableText";
import { useAppSelector } from "store/index";
import { selectCurrentUserIsMPAdmin } from "store/selectors";

export const STUDENT_DEMO_DEFAULT_PROMPT = `Take the following as context for my subsequent prompts.
Pretend that I am a fourth-year college student. Unless I tell you otherwise,
I'm planning a career in data science for the health care industry. I need your
help highlighting strengths from my academic background to help me get a job
after graduation. I may or may not be able to provide you with specifics about a
particular job. As you can see in the attached assessment at the bottom of this context.
I have earned proficiency scores on specific learning outcomes across a range of courses and
work products. In order for your advice to be as specific and actionable as
possible, feel free to use your imagination about what each work product
actually consisted of. Don't mention any of this context in your replies. Pretend
that, in addition to the score data, you have other deep knowledge of my academic
work extending over multiple years. For example, you know which work I did in
groups (vs. as an individual), and you have access to course materials and
assignment descriptions for all the work I did.
The learning outcome descriptions are as follows:

#behavior-explanation: Analyze how internal mental states, incentives and disincentives influence behavior and interactions in a social system.
#audience:Tailor oral and written work by considering the situation and perspective of the people receiving it. Different audiences have different background knowledge, interests, goals, world-views and perspectives. To communicate effectively, one must recognize such characteristics of an audience and tailor one’s messages for them. When an audience consists of people who have different levels of knowledge, goals, interests, or motivations, one cannot reach all of them equally effectively with the same delivery. Learning about and carefully considering an audience’s point of view can help one design communications that they can grasp, will appeal to their interests, and will be perceived as compelling.
#purpose: Identify and evaluate underlying goals and the values on which they are based, as well as the guiding principles that determine how an individual or group will try to attain these goals.
#teamwork: Analyze and apply team roles and skills that result in effective teamwork.
#professionalism: Follow established guidelines to present yourself and your work products professionally.
#science-of-learning: Evaluate and use effective strategies to learn or teach specific types of material.
#emotional-iq: Use emotional intelligence to interact effectively.
#project-navigation: Design and implement a plan to navigate a project effectively.
#leadership: Apply principles of effective leadership.
`;

const STUDENT_DEMO_PROMPT_KEY = "ForumStudentDemoPrompt";

export const getStudentDemoPrompt = () =>
  localStorage.getItem(STUDENT_DEMO_PROMPT_KEY) || STUDENT_DEMO_DEFAULT_PROMPT;

export const setStudentDemoPrompt = (value: string) =>
  localStorage.setItem(STUDENT_DEMO_PROMPT_KEY, value);

export const StudentDemoAIPrompt: FC<{ readOnly?: boolean }> = ({ readOnly = false }) => {
  const isMpAdmin = useAppSelector(selectCurrentUserIsMPAdmin);
  const [showExpanded, setShowExpanded] = useState(false);

  const studentDemoPrompt = getStudentDemoPrompt();

  if (!isMpAdmin) {
    return null;
  }

  return (
    <Card className="body-s my-5 w-full">
      <h3>AI Student Demo Prompt</h3>

      <HoverableToolbar showEditButton={!readOnly} uncentered onEditModeChange={setShowExpanded}>
        <ExpandableText className="w-full" lines={5} showExpanded={showExpanded}>
          <HoverableEditField
            className="pl-0 text-black-tint-20"
            label="AI Student Demo Prompt"
            textSize="s"
            value={studentDemoPrompt}
            multiline
            onValueChanged={setStudentDemoPrompt}
          />
        </ExpandableText>
      </HoverableToolbar>
    </Card>
  );
};
